import _ from 'lodash';
import { Box, Button, Divider, Stack, TableCell, TableRow, Typography } from '@mui/material';
import { useEffect, useState, useMemo } from 'react';
import { useAppDispatch, useAppSelector } from 'hooks/redux.hooks';
import { selectImageUrl, selectTranslations } from 'state/reducers/translation.reducers';
import { HeaderPage1 } from 'theme/elements/Headers';
import Add from '@mui/icons-material/Add';
import AddBankAccount from './AddBankAccount';
import { getUserPermission } from 'state/reducers/user.reducers';
import { UserPermission } from 'constants/user.constants';
import { getExistingBankAccounts, getMessage, isLoading, setMessage } from 'state/reducers/bank-account.reducers';
import { addBankAccount, getBankAccountsByCompanyId } from 'services/bank-account.services';
import { getBankList } from 'state/reducers/global.reducers';
import { Tooltip } from 'theme/elements/Tooltip';
import BasicTable from 'components/BasicTable';
import { columnType } from 'components/BasicTable/types';
import {
  BankAccountColumns,
  BankAccountDefaultSort,
  BankAccountId,
  BankAccountTableColumns,
} from 'constants/bank-account.constants';
import { useNavigate } from 'react-router';
import { FormButton, GeneralButton } from 'theme/elements/Buttons';
import { pagesEnum, userMesaageType, userMessage } from 'constants/ui.constants';
import WaitingMessage from 'components/WaitingMessage';
import { getMobile } from 'state/reducers/ui.reducers';
import WarningDialog from 'components/WarningDialog';

type obj = { [key: string]: string | number };
type objArray = obj[];

export default function BankAccounts() {
  const dispatch = useAppDispatch();
  const t = useAppSelector(selectTranslations);
  const navigate = useNavigate();
  const userPermission = useAppSelector(getUserPermission);
  const baseBankAccounts = useAppSelector(getExistingBankAccounts);
  const isBankAccountLoading = useAppSelector(isLoading);
  const bankUserMessage: userMessage = useAppSelector(getMessage);
  const bankList = useAppSelector(getBankList);
  const isMobile = useAppSelector(getMobile);
  const [tableView, setTableView] = useState(true);
  const [showTooltip, setShowTooltip] = useState(false);
  const prefixFields = 'page.settings.bank_accounts.';
  const imageUrl = useAppSelector(selectImageUrl);

  const bankAccountsTableColumns = () => {
    let columns: columnType[] = [];
    _.keys(BankAccountTableColumns).forEach((element) => {
      let props: columnType = {
        fieldId: BankAccountId,
        name: element,
        title: t(prefixFields + 'table.column.' + _.snakeCase(element) + '.text'),
        type: BankAccountTableColumns[element],
      };
      columns.push(props);
    });
    return columns;
  };
  const foramtValues = (bankAccounts: objArray) => {
    let list: objArray = [];
    bankAccounts.forEach((bankAccount) => {
      let ba: obj = {
        [BankAccountId]: _.get(bankAccount, BankAccountId),
      };
      _.keys(BankAccountTableColumns).forEach((key) => {
        switch (key) {
          case BankAccountColumns.bankName:
            ba[key] = bankAccount.bankName[BankAccountColumns.bankName];
            break;
          default:
            ba[key] = bankAccount[key];
        }
      });
      list.push(ba);
    });
    return list;
  };

  const submitForm = (
    accountOwnerName: string,
    accountNumber: string,
    branchNumber: string,
    bankId: number,
    file: File
  ) => {
    dispatch(addBankAccount(accountOwnerName, accountNumber, branchNumber, bankId, file));
  };

  useEffect(() => {
    if (bankUserMessage.type === userMesaageType.SUCCESS && !bankUserMessage.exist) {
      setTableView(true);
      setShowTooltip(true);
    }
  }, [bankUserMessage]);

  const openDialog = useMemo(() => {
    return bankUserMessage.exist && bankUserMessage.type === userMesaageType.ERROR;
  }, [bankUserMessage]);

  if (isBankAccountLoading) {
    return <WaitingMessage title="getting there..." imageKey="page.settings.bank_accounts.loading.icon" />;
  }

  return (
    <>
      {tableView && (
        <>
          <Stack spacing={5}>
            <Stack
              justifyContent={'space-between'}
              direction={{ xs: 'column', sm: 'row' }}
              spacing={{ xs: 2, sm: 0 }}
              divider={isMobile && <Divider orientation="horizontal" flexItem />}
            >
              <HeaderPage1 title={t('page.settings.bank_accounts.title')} />
              {(userPermission === UserPermission.SIGNER ||
                userPermission === UserPermission.OPERATIONER) && (
                  <Button
                    variant="contained"
                    startIcon={imageUrl(prefixFields + 'add_new_button.icon') || <Add />}
                    onClick={() => {
                      if (isMobile) {
                        navigate(`/${pagesEnum.SETTINGS}/addBankAccount`);
                      } else {
                        setTableView(false);
                      }
                    }}
                  >
                    {t(prefixFields + 'add_new_button.text')}
                  </Button>
                )}
            </Stack>
            <BasicTable
              fieldId="id"
              rowsClass="hoverButton"
              data={foramtValues(baseBankAccounts)}
              columns={bankAccountsTableColumns()}
              defaultSortBy={BankAccountDefaultSort}
              searchColumns={[]}
            />
          </Stack>
        </>
      )}
      {showTooltip && (
        <Tooltip
          dismissLabel={t(prefixFields + 'tooltip.dismiss_button.text')}
          handleClick={() => setShowTooltip(false)}
          styleObj={{ right: '5%', bottom: '5%' }}
        >
          <Typography variant="boldSmall">{t(prefixFields + 'create.success_tooltip.text')}</Typography>
        </Tooltip>
      )}
      {!tableView && <AddBankAccount handleCancel={() => setTableView(true)} />}
      {openDialog && (
        <WarningDialog
          open={openDialog}
          title={t(bankUserMessage.text || '')}
          subTitle={bankUserMessage.subText}
          handleCancel={() => {
            dispatch(setMessage({ exist: false }));
          }}
        />
      )}
    </>
  );
}
